import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Gift, Heart, Phone, ArrowLeft, CheckCircle, Laptop2, Shield, Star, Euro, Minus, Plus, User, MessageSquare } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "../hooks/use-toast";
import { useSEO } from "../hooks/useSEO";

const MIN_AMOUNT = 10;
const MAX_AMOUNT = 500;
const PRESET_AMOUNTS = [25, 50, 75, 100, 150];

const Gutschein = () => {
  useSEO({
    title: "IT-Support Geschenkgutschein | Klartech Müritz",
    description: "Verschenken Sie professionelle IT-Hilfe - ideal für Eltern oder Großeltern, die Unterstützung mit Computer, Smartphone oder Tablet benötigen.",
  });

  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [senderName, setSenderName] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");

  const handleAmountChange = (newAmount: number) => {
    const clampedAmount = Math.max(MIN_AMOUNT, Math.min(MAX_AMOUNT, newAmount));
    setAmount(clampedAmount);
    setCustomAmount(clampedAmount.toString());
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= MIN_AMOUNT && numValue <= MAX_AMOUNT) {
      setAmount(numValue);
    }
  };

  const handlePurchase = async () => {
    if (amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
      toast({
        title: "Ungültiger Betrag",
        description: `Bitte wählen Sie einen Betrag zwischen ${MIN_AMOUNT}€ und ${MAX_AMOUNT}€.`,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-gift-voucher-checkout", {
        body: { 
          amount,
          recipientName: recipientName.trim() || undefined,
          senderName: senderName.trim() || undefined,
          personalMessage: personalMessage.trim() || undefined,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        title: "Fehler",
        description: error.message || "Es gab ein Problem beim Erstellen der Bestellung. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3 md:py-4">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_tech-support-mvp/artifacts/88uemucq_LogoKlartech%20%281%29.png"
              alt="Klartech Müritz Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Zurück zur Startseite
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-12 md:py-20">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-5 py-2 text-red-700 font-semibold mb-6">
            <Gift className="h-5 w-5" />
            Geschenkidee
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
            IT-Hilfe <span className="text-[--brand-primary]">verschenken</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Das perfekte Geschenk für Eltern oder Großeltern: Professionelle Unterstützung bei Computer, Smartphone oder Tablet-Fragen.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 items-start">
          {/* Voucher Card */}
          <Card className="overflow-hidden border-2 border-[--brand-primary]/20 shadow-xl">
            <div className="bg-gradient-to-br from-[--brand-primary] to-blue-700 p-8 text-white">
              <div className="flex items-center justify-between mb-4">
                <Gift className="h-12 w-12" />
                <div className="text-right">
                  <div className="text-sm opacity-80">Geschenkgutschein</div>
                  <div className="text-4xl font-black">{amount}€</div>
                </div>
              </div>
              <div className="text-2xl font-bold mb-2">IT-Support Gutschein</div>
              <div className="text-blue-100">Klartech Müritz</div>
            </div>
            <CardContent className="p-6 space-y-6">
              {/* Amount Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Euro className="h-4 w-4" />
                  Gutscheinwert wählen
                </Label>
                
                {/* Preset Amounts */}
                <div className="flex flex-wrap gap-2">
                  {PRESET_AMOUNTS.map((presetAmount) => (
                    <Button
                      key={presetAmount}
                      variant={amount === presetAmount ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleAmountChange(presetAmount)}
                      className={amount === presetAmount ? "bg-[--brand-primary] hover:bg-[--brand-primary-dark]" : ""}
                    >
                      {presetAmount}€
                    </Button>
                  ))}
                </div>

                {/* Custom Amount Input */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleAmountChange(amount - 5)}
                    disabled={amount <= MIN_AMOUNT}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <div className="relative flex-1">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min={MIN_AMOUNT}
                      max={MAX_AMOUNT}
                      value={customAmount || amount}
                      onChange={(e) => handleCustomAmountChange(e.target.value)}
                      onBlur={() => {
                        if (!customAmount || parseInt(customAmount) < MIN_AMOUNT) {
                          handleAmountChange(MIN_AMOUNT);
                        } else if (parseInt(customAmount) > MAX_AMOUNT) {
                          handleAmountChange(MAX_AMOUNT);
                        }
                      }}
                      className="pl-9 text-center text-lg font-bold"
                      placeholder="Betrag eingeben"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleAmountChange(amount + 5)}
                    disabled={amount >= MAX_AMOUNT}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Min. {MIN_AMOUNT}€ – Max. {MAX_AMOUNT}€
                </p>
              </div>

              {/* Recipient & Sender Info */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="recipientName" className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Name des Beschenkten
                  </Label>
                  <Input
                    id="recipientName"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="z.B. Oma Erika"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="senderName" className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Ihr Name (Absender)
                  </Label>
                  <Input
                    id="senderName"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="z.B. Familie Müller"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="personalMessage" className="text-sm font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Persönliche Nachricht (optional)
                  </Label>
                  <Textarea
                    id="personalMessage"
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    placeholder="z.B. Alles Gute zum Geburtstag! Damit bekommst Du endlich Hilfe mit dem Tablet."
                    rows={2}
                    className="mt-1"
                    maxLength={200}
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {personalMessage.length}/200 Zeichen
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-2 py-4 border-t border-b border-slate-100">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Einlösbar für alle IT-Dienstleistungen</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Vor Ort oder als Remote-Support</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">12 Monate gültig</span>
                </div>
              </div>

              <Button
                onClick={handlePurchase}
                disabled={loading}
                className="w-full bg-[--brand-primary] hover:bg-[--brand-primary-dark] text-white py-6 text-lg font-bold rounded-xl"
              >
                {loading ? (
                  "Wird geladen..."
                ) : (
                  <>
                    <Gift className="mr-2 h-5 w-5" />
                    Gutschein für {amount}€ kaufen
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-slate-500">
                Sichere Zahlung über Stripe • Sofortiger E-Mail-Versand
              </p>
            </CardContent>
          </Card>

          {/* Benefits Section */}
          <div className="space-y-6">
            <Card className="p-6 bg-gradient-to-br from-pink-50 to-red-50 border-pink-200">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-pink-100">
                  <Heart className="h-6 w-6 text-pink-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Das perfekte Geschenk
                  </h3>
                  <p className="text-slate-600">
                    Ihre Eltern oder Großeltern kämpfen mit dem Smartphone? Der Computer macht Probleme? Schenken Sie professionelle Hilfe – ohne Fachsprache, mit viel Geduld.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-blue-100">
                  <Laptop2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Was wir gemeinsam lösen
                  </h3>
                  <ul className="text-slate-600 space-y-1">
                    <li>• Smartphone & Tablet einrichten</li>
                    <li>• WhatsApp & Video-Telefonie erklären</li>
                    <li>• Computer-Probleme beheben</li>
                    <li>• Sicherheit & Datenschutz</li>
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-green-100">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    So funktioniert's
                  </h3>
                  <ol className="text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Betrag wählen & Namen eingeben</li>
                    <li>Sicher online bezahlen</li>
                    <li>PDF-Gutschein per E-Mail erhalten</li>
                    <li>Ausdrucken & verschenken</li>
                  </ol>
                </div>
              </div>
            </Card>

            {/* Contact Alternative */}
            <div className="text-center p-6 bg-slate-100 rounded-xl">
              <p className="text-slate-600 mb-3">
                Fragen? Oder lieber telefonisch bestellen?
              </p>
              <a
                href="tel:+4915128958006"
                className="inline-flex items-center gap-2 text-[--brand-primary] font-bold hover:underline"
              >
                <Phone className="h-5 w-5" />
                01512 895 800 6
              </a>
            </div>
          </div>
        </div>

        {/* Trust Section */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-4 bg-white rounded-xl px-8 py-4 border border-slate-200 shadow-sm">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
              ))}
            </div>
            <div className="text-left">
              <div className="font-bold text-slate-800">Zufriedene Kunden</div>
              <div className="text-sm text-slate-600">in Waren (Müritz) und Umgebung</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 py-8">
        <div className="mx-auto max-w-7xl px-6 text-center text-slate-600">
          <p>© {new Date().getFullYear()} Klartech Müritz. Alle Rechte vorbehalten.</p>
          <div className="mt-2 flex justify-center gap-4 text-sm">
            <Link to="/impressum" className="hover:text-[--brand-primary]">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[--brand-primary]">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Gutschein;
